import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { OpenModalHookType } from '@hooks/useOpenModal';
import Authorization, { Approver } from '@components/Utilities/Authorization';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage, showAlertWarning } from '@utils/Alerts/Message';
import { currencyFormatter } from '@utils/Numbers';
import InventoryServ from '@api/Inventory/InventoryServ';
import { SHRINKAGE_REASON_LABELS, SHRINKAGE_REASONS, ShrinkageReason } from '@api/Inventory/InventoryMovement';
import {
    buildShrinkageEntries,
    estimateShrinkageAmount,
    isShrinkageNoteRequired,
    ShrinkageLine,
    validateShrinkageDraft
} from '@api/Inventory/InventoryShrinkage';
import {
    BranchOfficeInventory,
    formatQuantity,
    getAvailableUnits
} from '@api/BranchOfficeInventories/BranchOfficeInventory';
import { Input } from '@api/Inputs/Input';

// ============================================================================
// REGISTRAR MERMA (plan de inventario F2, pedido del negocio el 13-oct-2026):
// "se tiró, caducó, se echó a perder". Varios insumos en una misma merma (una
// charola que se cae lleva tres cosas), motivo de la lista (+ "Otro" con nota),
// y el PIN de un gerente / admin de sucursal / admin: la tablet es compartida y
// el PIN es el candado (§8.4). Quién capturó y quién autorizó quedan en el
// movimiento. El importe de aquí es aproximado: el server valúa de verdad.
// ============================================================================

interface ModalRegisterShrinkageProps {
    modal: OpenModalHookType;
    branchOfficeId: string;
    /** Los renglones de la sucursal (con el insumo del catálogo): de aquí se elige. */
    inventory: BranchOfficeInventory[];
    onSaved: () => void;
}

const emptyLine = (): ShrinkageLine => ({ inputId: '', quantity: '' });

export const ModalRegisterShrinkage: React.FunctionComponent<ModalRegisterShrinkageProps> = ({
    modal,
    branchOfficeId,
    inventory,
    onSaved
}) => {
    const { loggedUser } = useSelector((state: RootReducer) => state.auth);
    const [lines, setLines] = useState<ShrinkageLine[]>([emptyLine()]);
    const [reason, setReason] = useState<ShrinkageReason | undefined>(undefined);
    const [note, setNote] = useState('');
    const [approver, setApprover] = useState<Approver | undefined>(undefined);

    const inputsById = useMemo(() => {
        const map = new Map<string, Input>();
        inventory.forEach((row) => row.input?.id && map.set(row.input.id, row.input));
        return map;
    }, [inventory]);
    const availableById = useMemo(() => {
        const map = new Map<string, number>();
        inventory.forEach((row) => row.input?.id && map.set(row.input.id, getAvailableUnits(row)));
        return map;
    }, [inventory]);
    const options = useMemo(
        () => inventory.filter((row) => !!row.input?.id).sort((a, b) => (a.input.name || '').localeCompare(b.input.name || '', 'es')),
        [inventory]
    );

    const reset = () => {
        setLines([emptyLine()]);
        setReason(undefined);
        setNote('');
        setApprover(undefined);
    };

    useEffect(() => {
        if (modal.modalOpen) reset();
    }, [modal.modalOpen]);

    const setLine = (index: number, changes: Partial<ShrinkageLine>) =>
        setLines((current) => current.map((line, i) => (i === index ? { ...line, ...changes } : line)));

    const estimate = estimateShrinkageAmount(lines, inputsById);
    const problem = validateShrinkageDraft(lines, reason, note, approver?.uid);

    const onSave = async () => {
        if (problem) {
            showAlertError(problem);
            return;
        }
        showLoader();
        try {
            const result = await InventoryServ.registerShrinkage(branchOfficeId, buildShrinkageEntries(lines), {
                reason: reason as ShrinkageReason,
                note: note.trim() || undefined,
                userId: loggedUser?.uid,
                authorizedBy: approver?.uid
            });
            showAlertMessage(
                `Merma registrada: ${result.items.length} insumo(s)${
                    result.totalAmount > 0 ? `, ${currencyFormatter(result.totalAmount)}` : ''
                }`
            );
            result.warnings.forEach((warning) => showAlertWarning(warning));
            onSaved();
            modal.close();
        } catch (error: any) {
            showAlertError(error?.response?.data?.message || error);
        }
        hideLoader();
    };

    return (
        <Dialog open={modal.modalOpen} onClose={modal.close} fullWidth maxWidth="sm" PaperProps={{ style: { borderRadius: 15 } }}>
            <DialogTitle>
                <Typography variant="h6" fontWeight={600}>
                    Registrar merma
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} pt={1}>
                    <Typography variant="body2" color="text.secondary">
                        Lo que se tiró, caducó o se echó a perder sale del inventario con su motivo. No toca la caja.
                    </Typography>

                    {lines.map((line, index) => {
                        const input = inputsById.get(line.inputId);
                        const unit = input?.unit?.unit || '';
                        const unitValue = Number(input?.unit?.value) || 0;
                        const quantity = Number(line.quantity) || 0;
                        const presentations =
                            input && unitValue > 1 && quantity > 0
                                ? `≈ ${formatQuantity(quantity / unitValue, 1)} × ${formatQuantity(Number(input.presentation?.value) || 0)} ${
                                      input.presentation?.unit || ''
                                  }`
                                : '';
                        const available = availableById.get(line.inputId);
                        return (
                            <Stack key={index} spacing={0.5}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Autocomplete
                                        sx={{ flex: 2 }}
                                        options={options}
                                        getOptionLabel={(row) => row.input?.name || ''}
                                        isOptionEqualToValue={(a, b) => a.input?.id === b.input?.id}
                                        value={options.find((row) => row.input?.id === line.inputId) || null}
                                        onChange={(_, row) => setLine(index, { inputId: row?.input?.id || '' })}
                                        renderInput={(params) => <TextField {...params} required label="Insumo" size="small" />}
                                    />
                                    <TextField
                                        required
                                        size="small"
                                        type="number"
                                        label="Cantidad"
                                        inputProps={{ min: 0, step: 'any', inputMode: 'decimal' }}
                                        value={line.quantity}
                                        onChange={(event) => setLine(index, { quantity: event.target.value })}
                                        InputProps={{ endAdornment: unit ? <InputAdornment position="end">{unit}</InputAdornment> : undefined }}
                                        sx={{ flex: 1 }}
                                    />
                                    <IconButton
                                        aria-label="Quitar insumo"
                                        disabled={lines.length === 1}
                                        onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                                    >
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </Stack>
                                {input && (
                                    <Typography variant="caption" color="text.secondary" pl={1}>
                                        {[
                                            presentations,
                                            available !== undefined ? `hay ${formatQuantity(available)} ${unit}` : ''
                                        ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </Typography>
                                )}
                            </Stack>
                        );
                    })}
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setLines((current) => [...current, emptyLine()])}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        Agregar otro insumo
                    </Button>

                    <FormControl fullWidth required>
                        <InputLabel id="shrinkage-reason-label">¿Qué pasó?</InputLabel>
                        <Select
                            labelId="shrinkage-reason-label"
                            label="¿Qué pasó?"
                            value={reason || ''}
                            onChange={(event) => setReason(event.target.value as ShrinkageReason)}
                        >
                            {SHRINKAGE_REASONS.map((code) => (
                                <MenuItem key={code} value={code}>
                                    {SHRINKAGE_REASON_LABELS[code]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        required={isShrinkageNoteRequired(reason)}
                        label={isShrinkageNoteRequired(reason) ? 'Escribe qué pasó' : 'Nota (opcional)'}
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                    />

                    {estimate > 0 && (
                        <Alert severity="warning">
                            {`Vas a dar de baja ${currencyFormatter(estimate)} aprox. en insumos (al costo de la sucursal).`}
                        </Alert>
                    )}

                    <Typography variant="body2" color="text.secondary">
                        La merma la autoriza con su PIN un gerente, admin de sucursal o admin. Quedan registrados quién la capturó
                        y quién la autorizó.
                    </Typography>
                    <Authorization requirePin onAuthorized={(who) => setApprover(who)} onUnauthorized={() => setApprover(undefined)} />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Stack width="100%" direction="row" spacing={1} justifyContent="flex-end">
                    <Button color="inherit" onClick={modal.close}>
                        Cancelar
                    </Button>
                    <Button variant="contained" color="primary" disabled={!!problem} onClick={() => void onSave()}>
                        Registrar merma
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};
